// Tableau de bord (spec § 11) : les 10 du jour, compteurs des fiches et du suivi commercial, projets à fort score, nouveautés de
// la semaine, classement des pays. Les chiffres viennent des vues `dashboard_*`, calculées avec les droits de
// l'utilisateur : un partenaire n'y voit que son territoire.
import { useAccountCurrency } from "./CurrencySelector";
import { projectName } from "./projectText";
import {
  useGetIdentity,
  useGetList,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { ChartColumn } from "lucide-react";
import { lazy, type ReactNode, Suspense, useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { MethodStamp } from "./MethodStamp";
import { ScoreStamp } from "./ScoreCard";
import type { Project } from "../types";
import { PhaseBadge } from "./PhaseBadge";
import { AskBar } from "./QuestionsPage";
import { DailyPicks } from "./DailyPicks";
import { MyDay } from "./MyDay";
import {
  countryName,
  formatBudget,
  formatDay,
  HIDE_OTHER,
} from "./projectUtils";
import { HelpTip } from "./HelpTip";

type Overview = {
  id: number;
  projects: number;
  projects_new_week: number;
  countries: number;
  projects_high_score: number;
  value_eur: number | null;
  projects_valued: number;
  projects_unconverted: number;
  rates_date: string | null;
  deals_active: number;
  deals_overdue: number;
  deals_without_action: number;
  deals_idle: number;
};

// Sources ayant apporté au moins une fiche, sur tout le radar (vue `dashboard_sources`).
type SourceCoverage = {
  id: number;
  official_sources: number;
  press_feeds: number;
};

type CountryRow = {
  id: string;
  projects: number;
  projects_high_score: number;
  value_eur: number | null;
};

// Formes des pays et tracé chargés à part : le reste du tableau de bord n'attend pas la carte.
const CountryMap = lazy(() => import("./CountryMap"));

const HIGH_SCORE = 80;
const listLink = (filter: object) =>
  `/projects?filter=${encodeURIComponent(JSON.stringify({ ...HIDE_OTHER, ...filter }))}`;
// Début de la fenêtre « cette semaine », arrondi au jour pour que la requête reste la même pendant la journée.
const weekStart = () => {
  const day = new Date();
  day.setHours(0, 0, 0, 0);
  day.setDate(day.getDate() - 7);
  return day.toISOString();
};

/** Accès au tableau de bord depuis l'en-tête de la liste sur téléphone (sur ordinateur, c'est l'accueil). */
export const DashboardLink = () => {
  const translate = useTranslate();
  return (
    <Button
      asChild
      variant="ghost"
      size="icon"
      className="rounded-full"
      aria-label={translate("resources.projects.dashboard.title")}
    >
      <Link to="/dashboard">
        <ChartColumn className="size-5" />
      </Link>
    </Button>
  );
};

export const ProjectDashboard = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const t = (key: string, options?: object) =>
    translate(`resources.projects.dashboard.${key}`, options);
  const { data } = useGetList<Overview>("dashboard_overview", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "ASC" },
  });
  const overview = data?.[0];
  const { data: coverage } = useGetList<SourceCoverage>("dashboard_sources", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "ASC" },
  });
  const sources = coverage?.[0];
  const { identity } = useGetIdentity();
  const firstName = String(identity?.firstName ?? "").trim();
  const since = weekStart();
  // Montants estimés dans la monnaie du compte, au cours de la BCE.
  const { estimate } = useAccountCurrency();

  return (
    <div className="mt-2 pb-6 flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight">
          {firstName ? t("greeting", { name: firstName }) : t("title")}
        </h2>
        <p className="max-w-3xl text-sm text-muted-foreground">{t("intro")}</p>
        {sources && (
          <p className="max-w-3xl text-sm text-muted-foreground">
            {t("sources_line", {
              official: sources.official_sources,
              press: sources.press_feeds,
            })}
          </p>
        )}
      </div>

      <AskBar />

      <MyDay />

      <DailyPicks />

      {overview && (
        <>
          <Sheet className="grid-cols-2 lg:grid-cols-5">
            <Figure
              to={listLink({})}
              value={overview.projects}
              label={t("projects")}
            />
            <Figure
              to={listLink({ "created_at@gte": since })}
              value={overview.projects_new_week}
              label={t("new_week")}
            />
            <Figure
              to={listLink({ "score@gte": HIGH_SCORE })}
              value={overview.projects_high_score}
              label={t("high_score", { score: HIGH_SCORE })}
              stamp={
                <>
                  <ScoreStamp />
                  <HelpTip topic="score" className="-ml-1" />
                </>
              }
            />
            <Figure value={overview.countries} label={t("countries")} />
            <Figure
              className="col-span-2 lg:col-span-1"
              value={
                overview.value_eur == null ? "–" : estimate(overview.value_eur)
              }
              label={t("value")}
              estimate={t("estimate")}
              note={t("value_note", {
                smart_count: overview.projects_valued,
                date: formatDay(overview.rates_date, locale),
              })}
            />
          </Sheet>

          <section className="flex flex-col gap-3">
            <SectionTitle>{t("map_title")}</SectionTitle>
            <Suspense
              fallback={
                <div className="aspect-[960/526] rounded-lg border border-border bg-card" />
              }
            >
              <CountryMap />
            </Suspense>
          </section>

          <section className="flex flex-col gap-3">
            <SectionTitle>{t("follow_up")}</SectionTitle>
            <Sheet className="grid-cols-2 lg:grid-cols-4">
              <Figure
                to="/deals"
                value={overview.deals_active}
                label={t("deals_active")}
              />
              <Figure
                to="/deals"
                value={overview.deals_overdue}
                label={t("deals_overdue")}
                alert={overview.deals_overdue > 0}
              />
              <Figure
                to="/deals"
                value={overview.deals_without_action}
                label={t("deals_without_action")}
                alert={overview.deals_without_action > 0}
              />
              <Figure
                to="/deals"
                value={overview.deals_idle}
                label={t("deals_idle", { days: 30 })}
                alert={overview.deals_idle > 0}
              />
            </Sheet>
          </section>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-8">
        <ProjectRanking
          title={t("top_projects")}
          more={listLink({ "score@gte": HIGH_SCORE })}
          filter={HIDE_OTHER}
        />
        <ProjectRanking
          title={t("new_projects")}
          more={listLink({ "created_at@gte": since })}
          filter={{ ...HIDE_OTHER, "created_at@gte": since }}
          empty={t("no_new")}
        />
      </div>

      <CountryRanking />
    </div>
  );
};

/** Feuille réglée : les chiffres sont des cases d'un même relevé, séparées par un filet, pas des cartes. */
const Sheet = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <section
    className={cn(
      "grid gap-px overflow-hidden rounded-lg border border-border bg-border",
      className,
    )}
  >
    {children}
  </section>
);

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <h3 className="text-sm font-bold">{children}</h3>
);

const Figure = ({
  value,
  label,
  note,
  estimate,
  stamp,
  to,
  alert,
  className,
}: {
  value: ReactNode;
  label: string;
  note?: string;
  estimate?: string;
  stamp?: ReactNode;
  to?: string;
  alert?: boolean;
  className?: string;
}) => {
  const body = (
    <>
      <span
        className={cn(
          "text-3xl font-bold leading-none tracking-tight tabular-nums",
          alert && "text-destructive",
        )}
      >
        {value}
      </span>
      <span className="mt-2 flex flex-wrap items-center gap-2 text-sm leading-snug text-muted-foreground">
        {label}
        {estimate && <MethodStamp method="inferred" label={estimate} />}
        {stamp}
      </span>
      {note && (
        <span className="mt-1 text-xs leading-snug text-muted-foreground">
          {note}
        </span>
      )}
    </>
  );
  const cell = cn("flex flex-col bg-card px-4 py-4", className);
  return to ? (
    <Link
      to={to}
      className={cn(
        cell,
        "transition-colors duration-(--motion-duration) hover:bg-muted/60",
      )}
    >
      {body}
    </Link>
  ) : (
    <div className={cell}>{body}</div>
  );
};

const ProjectRanking = ({
  title,
  more,
  filter,
  empty,
}: {
  title: string;
  more: string;
  filter: object;
  empty?: string;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { data, isPending } = useGetList<Project>("projects", {
    filter,
    pagination: { page: 1, perPage: 8 },
    sort: { field: "score", order: "DESC" },
  });
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <SectionTitle>{title}</SectionTitle>
        <Link
          to={more}
          className="text-sm text-muted-foreground hover:text-foreground hover:underline"
        >
          {translate("resources.projects.dashboard.see_all")}
        </Link>
      </div>
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {!isPending && !data?.length ? (
          <p className="text-sm text-muted-foreground px-4 py-6 text-center">
            {empty}
          </p>
        ) : (
          <ul className="divide-y">
            {data?.map((project) => (
              <li key={project.id}>
                <Link
                  to={`/projects/${project.id}/show`}
                  className="flex gap-3 px-4 py-3 transition-colors duration-(--motion-duration) hover:bg-muted/60"
                >
                  <span className="w-8 shrink-0 text-lg font-bold leading-6 tabular-nums">
                    {project.score ?? "–"}
                  </span>
                  <div className="min-w-0 flex-1 flex flex-col gap-1">
                    <span className="text-sm font-medium leading-snug line-clamp-2">
                      {projectName(project, locale)}
                    </span>
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                      <PhaseBadge phase={project.phase} />
                      <span>{countryName(project.country, locale)}</span>
                      <span>{formatBudget(project, locale)}</span>
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

const RANKED_COUNTRIES = 12;

const CountryRanking = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const t = (key: string, options?: object) =>
    translate(`resources.projects.dashboard.${key}`, options);
  const [expanded, setExpanded] = useState(false);
  const { data: all } = useGetList<CountryRow>("dashboard_countries", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "projects", order: "DESC" },
  });
  // Montants estimés dans la monnaie du compte, au cours de la BCE.
  const { estimate } = useAccountCurrency();
  if (!all?.length) return null;
  // Les premiers pays, puis tous sur demande : aucun pays de la base ne doit manquer au classement.
  const data = expanded ? all : all.slice(0, RANKED_COUNTRIES);
  const max = Math.max(...all.map((row) => row.projects));
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>{t("countries_ranking")}</SectionTitle>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground">
            <tr className="border-b">
              <th className="text-left font-normal pl-3 pr-1 sm:px-4 py-2">
                {t("country")}
              </th>
              <th className="text-left font-normal px-1 sm:px-2 py-2 sm:w-full">
                {t("projects_short")}
              </th>
              <th className="text-right font-normal px-1 sm:px-2 py-2">
                {t("high_score_short", { score: HIGH_SCORE })}
              </th>
              <th className="text-right font-normal pr-3 pl-1 sm:px-4 py-2">
                <span className="inline-flex items-center gap-0.5">
                  {t("value_short")}
                  <HelpTip topic="country_value" />
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.map((row) => (
              <tr key={row.id}>
                <td className="pl-3 pr-1 sm:px-4 py-2 sm:whitespace-nowrap">
                  <Link
                    to={listLink({ country: row.id })}
                    className="hover:underline"
                  >
                    {countryName(row.id, locale)}
                  </Link>
                </td>
                <td className="px-1 sm:px-2 py-2">
                  <div className="flex items-center gap-2">
                    <span className="tabular-nums w-7 text-right shrink-0">
                      {row.projects}
                    </span>
                    <span
                      className="hidden sm:block h-1.5 rounded-full bg-foreground/25"
                      style={{ width: `${(row.projects / max) * 100}%` }}
                    />
                  </div>
                </td>
                <td className="px-1 sm:px-2 py-2 text-right tabular-nums">
                  {row.projects_high_score}
                </td>
                <td className="pr-3 pl-1 sm:px-4 py-2 text-right tabular-nums whitespace-nowrap">
                  {row.value_eur == null
                    ? "–"
                    : estimate(row.value_eur).replace(/^≈ /, "")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {all.length > RANKED_COUNTRIES && (
          <div className="border-t px-1 py-1">
            <Button
              variant="link"
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded
                ? t("countries_less")
                : t("countries_all", { count: all.length })}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
